export type EngineView = "gallery" | "collections" | "creators" | "upload" | "rank" | "download" | "play";

export interface EngineNavItem {
  label: "Discover" | "Workshop" | "My Library" | "Collections" | "Rank" | "Download";
  view: EngineView;
  translationKey: "nav.discover" | "nav.workshop" | "nav.library" | "nav.collections" | "nav.rank" | "nav.download";
  path: "/pets" | "/creators" | "/library" | "/collections" | "/rank" | "/download";
}

export const ENGINE_NAV_ITEMS: readonly EngineNavItem[] = [
  { label: "Discover", view: "gallery", translationKey: "nav.discover", path: "/pets" },
  { label: "Workshop", view: "creators", translationKey: "nav.workshop", path: "/creators" },
  { label: "My Library", view: "upload", translationKey: "nav.library", path: "/library" },
  { label: "Collections", view: "collections", translationKey: "nav.collections", path: "/collections" },
  { label: "Rank", view: "rank", translationKey: "nav.rank", path: "/rank" },
  { label: "Download", view: "download", translationKey: "nav.download", path: "/download" },
];

export interface ResolvedEngineRoute {
  view: EngineView;
  petId?: string;
  collectionSlug?: string;
  creatorHandle?: string;
  tag?: string;
  contentPath?: string;
  rankSub?: "connect" | "me";
}

export function toRouteSlug(value: string): string {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export function getViewPath(view: EngineView): string {
  if (view === "play") return "/play";
  return ENGINE_NAV_ITEMS.find((item) => item.view === view)?.path ?? "/pets";
}

export function resolveEngineRoute(pathname: string): ResolvedEngineRoute {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const petMatch = normalized.match(/^\/pets\/([a-z0-9]+(?:-[a-z0-9]+)*)$/);
  if (petMatch) return { view: "gallery", petId: petMatch[1] };
  const collectionMatch = normalized.match(/^\/collections\/([a-z0-9]+(?:-[a-z0-9]+)*)$/);
  if (collectionMatch) return { view: "gallery", collectionSlug: collectionMatch[1] };
  const creatorMatch = normalized.match(/^\/creators\/([a-z0-9]+(?:-[a-z0-9]+)*)$/);
  if (creatorMatch) return { view: "gallery", creatorHandle: creatorMatch[1] };
  const tagMatch = normalized.match(/^\/tags\/([a-z0-9]+(?:-[a-z0-9]+)*)$/);
  if (tagMatch) return { view: "gallery", tag: tagMatch[1] };
  if (
    normalized === "/create-codex-pet" ||
    normalized === "/install-codex-pet" ||
    /^\/(?:tools|docs|guides)\/[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized)
  ) return { view: "gallery", contentPath: normalized };
  if (normalized === "/collections") return { view: "collections" };
  if (normalized === "/creators") return { view: "creators" };
  if (normalized === "/library") return { view: "upload" };
  if (normalized === "/rank") return { view: "rank" };
  if (normalized === "/rank/connect") return { view: "rank", rankSub: "connect" };
  if (normalized === "/rank/me") return { view: "rank", rankSub: "me" };
  if (normalized === "/download") return { view: "download" };
  if (normalized === "/play") return { view: "play" };
  return { view: "gallery" };
}

export function getViewTitle(view: EngineView): string {
  switch (view) {
    case "gallery": return "Discover";
    case "creators": return "Workshop";
    case "collections": return "Collections";
    case "upload": return "My Library";
    case "rank": return "Rank";
    case "download": return "Download";
    case "play": return "Desktop Pet";
  }
}
